import React, { useEffect, useState } from 'react';

type Mode = 'what' | 'why';

interface Props {
  mode: Mode;
  onClose: () => void;
}

const WHAT_SECTIONS = [
  {
    label: 'Definition',
    text: 'Arete (\u1F00\u03C1\u03B5\u03C4\u03AE) is a concept from ancient Greek philosophy referring to the pursuit of excellence through the full development of one\u2019s capacities.',
  },
  {
    label: 'Health Context',
    text: 'Health rarely improves because of a single intervention. Mental and physical wellbeing emerge from the interaction of many systems \u2014 including sleep, physical activity, nutrition, stress physiology, social connection, and daily habits.',
  },
  {
    label: 'Why This Matters',
    text: 'Looking at one metric in isolation often misses the bigger picture. Understanding how these systems interact can lead to better insight into mental health, overall wellbeing, and treatment response.',
  },
  {
    label: 'What Arete Health Does',
    text: 'Arete Health brings these pieces together by integrating lifestyle data, behavioral patterns, and health signals into one unified view. The goal is to make the broader system easier for patients and clinicians to understand.',
  },
] as const;

const WHY_SECTIONS = [
  {
    label: 'The Problem',
    text: 'Most health tools focus on one thing \u2014 a step count, a sleep score, a lab result. But health does not work that way. Poor sleep raises cortisol, which drives cravings, which affects nutrition, which worsens mood, which disrupts sleep again. Every system is connected. Looking at any one factor in isolation misses the bigger picture.',
  },
  {
    label: 'What Arete Does Differently',
    text: 'Arete integrates data across the six pillars that drive wellbeing \u2014 sleep, movement, nutrition, stress resilience, social connection, and substance use. By pulling together wearable data, lab results, and self-reported check-ins, it creates a complete picture of how these systems interact for each patient.',
  },
  {
    label: 'For Clinicians',
    text: 'A clinician seeing a patient with worsening depression might not know that the patient has also been sleeping four hours a night, stopped exercising, and increased alcohol use. Arete surfaces those patterns \u2014 not as a diagnosis, but as signal. It helps clinicians and patients understand what is actually happening so treatment decisions can be more informed and more precise.',
  },
  {
    label: 'For Patients',
    text: 'Health improves when you can see the connections. A weekly check-in takes two minutes and builds a longitudinal picture of how lifestyle choices shape how you feel. Over time, the patterns become clear \u2014 and that clarity creates leverage for real change.',
  },
] as const;

const MODAL_META: Record<Mode, { title: string; subtitle: string; sections: readonly { label: string; text: string }[] }> = {
  what: { title: 'What is Arete?', subtitle: 'An integrated approach to health', sections: WHAT_SECTIONS },
  why:  { title: 'Why Arete?', subtitle: 'The pursuit of excellence across the whole person', sections: WHY_SECTIONS },
};

export default function AreteExplainer({ mode, onClose }: Props) {
  const [visible, setVisible] = useState(false);
  const { title, subtitle, sections } = MODAL_META[mode];

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  function handleClose() {
    setVisible(false);
    setTimeout(onClose, 200);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`} />
      <div
        className={`relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto transition-all duration-200 ${
          visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-2xl px-6 pt-6 pb-4 border-b border-gray-100/80 z-10">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-arete-navy tracking-tight">
                {title}
              </h2>
              <p className="text-sm text-arete-muted mt-0.5">
                {subtitle}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-arete-muted hover:text-arete-navy p-1 -mr-1 transition-colors"
            >
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {sections.map(({ label, text }) => (
            <div key={label}>
              <p className="section-label mb-1.5">{label}</p>
              <p className="text-sm text-arete-navy/80 leading-relaxed">
                {text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
